export const gitNotes = [
  {
    id: 701,
    topic: "git",
    seriesKey: "git",
    title: "Introduction to Git ",
    description:
      "Defining Git as a distributed version control system, understanding its benefits, and performing the initial installation and configuration steps.",
    content: `
##  What is Git?
Git is a **distributed version control system (DVCS)** designed to track changes in source code during software development. It allows every developer to have a full copy of the entire project history.

### History & Developer Info
- Creator:Linus Torvalds (also created the Linux kernel).
- Year Released:2005.

### Why Git is Important (Version Control Benefits)
1.  **Safety & Rollback:** Easily revert to any previous, stable state of the code.
2.  **Collaboration:** Multiple developers can work simultaneously on the same project without conflict.
3.  **Branching:** Allows development of new features in isolation from the main codebase.
4.  **DVCS Advantage:** Operations (like commit, diff, log) are extremely fast because they use your local history.

##  Installation and Setup
### Initial Configuration
After installing Git, you must configure your identity. This information is permanently attached to every commit you make.

\`\`\`bash
# 1. Set your global username
git config --global user.name "Your Name"

# 2. Set your global email
git config --global user.email "you@example.com"

# 3. Verify your settings
git config --list
\`\`\`

---
### The Three States (The Git Life Cycle)
Every file in a Git repository is in one of three states:
1. **Working Directory/Tree:** The actual files you are editing (Modified).
2. **Staging Area/Index:** Where you prepare a snapshot of changes for the next commit (Staged).
3. **Local Repository (HEAD):** Where Git stores your finalized changes as a Commit (Committed).
      `,
    tags: ["Git", "Introduction", "DVCS", "Configuration", "Three States"],
    date: "2024-12-05",
  },
  {
    id: 702,
    topic: "git",
    seriesKey: "git",
    title:
      "Basic Local Workflow ",
    description:
      "Mastering the fundamental Git commands used daily to start a repository, track changes, and save work permanently.",
    content: `
##  Essential Git Commands 
This workflow is the core of using Git locally: **Modify** ➡️ **Stage** ➡️ **Commit**.
| Command | Purpose | Example |
| :--- | :--- | :--- |
| \`git init\` | Initializes a new, empty Git repository in the current directory. | \`git init\` |
| \`git status\` | Shows the state of the working directory and staging area (the most-used command). | \`git status\` |
| \`git add\`| Moves changes from the Modified state to the Staged state. | \`git add index.html\` or \`git add .\` |
| \`git commit\`| Records the staged snapshot permanently into the repository history. | \`git commit -m "Feat: Add header section"\` |
| \`git log\` | Shows the commit history for the current branch. | \`git log --oneline\` |
---
### Ignoring Files
You don't want to track every file (e.g., temporary files, compiled code, or secret keys).
- \`touch .gitignore\`
- Contents: List files or patterns to exclude from Git's tracking (e.g., \`node_modules/\`, \`.log\`).
- Check:Untracked files listed in \`.gitignore\` will not show up in \`git status\`.
      `,
    tags: [
      "Git",
      "Workflow",
      "git init",
      "git status",
      "git add",
      "git commit",
      ".gitignore",
    ],
    date: "2024-12-05",
  },
  {
    id: 703,
    topic: "git",
    seriesKey: "git",
    title: "Remote Repositories and Synchronization ",
    description:
      "Connecting local repositories to remote servers (like GitHub) and synchronizing changes with essential network commands.",
    content: `
##  Remote Repositories
A **Remote Repository** is the shared version of the project hosted on the internet (e.g., GitHub, GitLab).
### Connecting and Cloning
| Command | Purpose | Example |
| :--- | :--- | :--- |
| \`git clone\` | Copies an existing remote repository to your local machine (includes the full history). | \`git clone <URL>\` |
| \`git remote add\`| Links your existing local repo to a new remote server. \`origin\` is the conventional name. | \`git remote add origin <URL>\` |
| \`git remote -v\` | Lists all existing remotes and their URLs. | \`git remote -v\` |
---
##  Synchronization Commands
These commands are used to move commits between your local repo and the remote repo.
| Command | Purpose | Effect |
| :--- | :--- | :--- |
| \`git fetch\`| Downloads new changes (commits) from the remote to your local repository. Does NOT update your local working files.| Safely inspect remote changes first. |
| \`git pull\` | Fetch + Merge. Downloads changes from the remote and immediately merges them into your current local branch. | Updates your local working files instantly. |
| \`git push\` | Uploads your local branch's commits to the remote repository. | Shares your work with the team. |
### Pushing for the First Time
When pushing a new branch, you must set the upstream tracking branch:
\`\`\`bash
# Pushes the local branch and sets 'origin/new-feature' as the tracking branch
git push -u origin new-feature
\`\`\`
      `,
    tags: ["Git", "Remote", "git clone", "git fetch", "git pull", "git push"],
    date: "2024-12-07",
  },
  {
    id: 704,
    topic: "git",
    seriesKey: "git",
    title: "Branching and Merging",
    description:
      "Learning how to isolate feature development, combine code with merging, and manage the branch lifecycle.",
    content: `
##  Branching Fundamentals
A **branch** is a lightweight, movable pointer to a commit. The default branch is usually **\`main\`** or \`master\`. They allow developers to safely work on new features or fixes without affecting the stable code.
### Branch Management Commands
| Command | Purpose | Example |
| :--- | :--- | :--- |
| \`git branch\`| Lists all local branches. | \`git branch\` |
| \`git switch <name>\` | Switches your working directory to an existing branch (modern command). | \`git switch main\` |
| \`git switch -c <name>\`| Shortcut:Creates and immediately switches to a new branch. | \`git switch -c feature/login\` |
---
##  Merging Branches
**Merging** integrates the changes from one branch into another, creating a single new **merge commit**.
**Example:** Merging \`feature/login\` into \`main\`:
1.Switch to the target branch: \`git switch main\`
2.Merge the source branch: \`git merge feature/login\`
### Handling Merge Conflicts 
A conflict occurs when two branches modify the same line of code. Git pauses the merge.
1.  **Resolve:** Manually edit the conflicted files (look for \`<<<<<<<\`, \`=======\`, \`>>>>>>>\` markers).
2.  **Stage:** \`git add <conflicted-file>\`
3.  **Commit:** \`git commit -m "Resolved merge conflict"\` to complete the merge.
### Deleting Branches
After a successful merge, you can safely delete the source branch.
\`\`\`bash
# Delete the local branch (-d is for 'delete', must be fully merged)
git branch -d feature/login
# Delete the remote branch
git push origin --delete feature/login
\`\`\`
      `,
    tags: [
      "Git",
      "Branching",
      "Merging",
      "git switch",
      "git branch",
      "Conflicts",
    ],
    date: "2024-12-06",
  },
  {
    id: 705,
    topic: "git",
    seriesKey: "git",
    title: "Stash, Revert, and Reset ",
    description:
      "Commands for correcting mistakes, discarding local changes, and temporarily saving work in progress.",
    content: `
##  The Three Ways to Undo
Mistakes are inevitable. Git provides different tools to undo changes based on how much history you want to keep or rewrite.
| Command | Effect | Use Case |
| :--- | :--- | :--- |
| \`git stash\` | Temporarily saves modified/staged work in a stack without committing. | You need to switch branches quickly but have unfinished work. |
| \`git revert\`| Creates a new commit that undoes the changes of a previous commit. History is preserved. | Safe way to undo changes on public/shared branches. |
| \`git reset\` | Moves the branch pointer back to an earlier commit, effectively discarding subsequent commits. Rewrites history. | Use for cleaning up private, local commits. Never on shared branches.|
---
### Stashing Commands
\`\`\`bash
# Save current modified and staged changes
git stash push -m "WIP: Feature X" 

# Reapply the last stashed changes and remove it from the stash stack
git stash pop 

# Discard all local changes
git restore . 
\`\`\`
### Example: Undoing the Last Commit
| Method | Command | Result |
| :--- | :--- | :--- |
| Safe Undo (New Commit) | \`git revert HEAD\` | Creates a commit that negates the changes of the last commit. |
| Rewrite History (Local Only) | \`git reset --soft HEAD~1\` | Un-commits the last change, keeping files staged. |
| Rewrite & Unstage | \`git reset HEAD~1\` | Un-commits the last change, keeping files unstaged (modified). |
      `,
    tags: [
      "Git",
      "Undo",
      "git reset",
      "git revert",
      "git stash",
      "Discarding Changes",
    ],
    date: "2024-12-06",
  },
  {
    id: 706,
    topic: "git",
    seriesKey: "git",
    title: "Advanced Workflow ",
    description:
      "Commands for creating cleaner, linear history and marking significant points in time.",
    content: `
##  Advanced History Management
### 1. Rebasing (\`git rebase\`)
**Rebasing** moves a sequence of commits to a new base commit. It creates a cleaner, linear history by reapplying commits one by one onto the target branch.
- When to use: To integrate upstream changes into your feature branch before merging, resulting in a tidy "straight line" history.
- Warning:NEVER rebase commits that have been pushed to a public/shared branch. Rebasing rewrites history and can cause problems for collaborators.
\`\`\`bash
# While on your feature branch, rebase your commits onto main
git rebase main
\`\`\`
### 2. Cherry-Picking (\`git cherry-pick\`)
**Cherry-picking** allows you to take a single commit from one branch and apply it to another branch.
- When to use:To apply a small, specific fix (hotfix) from a development branch to the stable branch without merging the entire feature.
\`\`\`bash
# Apply commit A1B2C3 from another branch onto the current branch
git cherry-pick A1B2C3
\`\`\`
---
##  Tags in Git
A **Tag** marks a specific point in history as important, typically used to denote software release versions (e.g., v1.0.0).
### Annotated Tags (Recommended)
Annotated tags include the tagger's name, email, date, and a message, making them robust for official releases.
\`\`\`bash
# Create an annotated tag
git tag -a v1.0.0 -m "Release version 1.0.0"

# Push tags to remote (they are not pushed by default)
git push origin --tags
\`\`\`
      `,
    tags: [
      "Git",
      "Advanced",
      "Rebasing",
      "Cherry-pick",
      "Tags",
      "Rewriting History",
    ],
    date: "2024-12-08",
  },
  {
    id: 707,
    topic: "git",
    seriesKey: "git",
    title: "Collaboration",
    description:
      "Understanding the modern team workflow and essential guidelines for effective version control.",
    content: `
##  Modern Collaboration Workflow
Most development teams use a **Branch + Pull Request (PR)** workflow with platforms like GitHub, GitLab, or Bitbucket.
1. **Branching:** Create a descriptive feature branch (e.g., \`feat/new-button\`).
2. **Commit & Push:** Commit your work locally and push the branch to the remote.
3. **Pull Request (PR):** Open a PR on the remote platform. This is a formal request to merge your work into the main branch.
4. **Code Review:** Teammates review the PR, suggest changes, and approve the code.
5. **Merge:** The PR is merged into \`main\`.
---
## Git Best Practices
1. **Atomic Commits:** Each commit should represent **one logical unit of change** (e.g., "Fix: typo in sidebar," "Feat: Add checkout form").
2. **Commit Often:** Commit small, self-contained changes frequently. This provides granular checkpoints for history and rollback.
3. **Branch for Everything:** **Never commit directly to \`main\` or \`master\`**. Use dedicated branches for every feature or fix.
4. **Meaningful Messages:** The subject line should be short and complete the sentence: "If applied, this commit will..."
5. **Git Hooks:** Use tools like \`pre-commit\` hooks to automatically run linters or formatters before a commit is finalized, ensuring code quality standards.
      `,
    tags: [
      "Git",
      "Best Practices",
      "Pull Requests",
      "Code Review",
      "Atomic Commits",
      "Hooks",
    ],
    date: "2024-12-08",
  },
];
